/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService } from './api.service';
import { AuthStoreService } from './authStore.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { ModulesEnum } from '../../enums';
import { Observable } from 'rxjs';

@Injectable()
export class DownloadService {
    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService,
        private httpClient: HttpClient,
        private logService: LogService,
    ) {
    }

    /**
     * Down a file from a URL.
     * @param url
     * @param type
     */
    public download(url: string, type: any): Observable<any> {
        return this.httpClient.get(
            url,
            {
                responseType: type,
                reportProgress: true,
                observe: 'events'
            }
        );
    }

    /**
     * Create Blob File.
     * @param fileContent
     * @param fileName
     * @param options - object containing type and/or callback
     */
    public generateDownload(fileContent: BlobPart, fileName: string, options?: any): void {
        const type = {};
        if (options?.type) {
            type['type'] = options.type;
        }
        const fileNameWithOptionalExtension = options?.extension ? fileName + options.extension : fileName;

        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(new Blob([fileContent], type));

        a.href = objectUrl;
        a.download = fileNameWithOptionalExtension;
        a.click();

        URL.revokeObjectURL(objectUrl);

        if (options?.callBack) {
            options.callBack();
        }
    }

    /**
     * Request a file to download
     * @param request
     * @param options
     */
    public downloadFile(request: any, options?: any): Observable<any> {
        return new Observable<any>(observer => {
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            const FQUrl = this.apiService.buildFQUrl(request.url, moduleAuth, request.appID);
            this.httpClient.get(
                FQUrl,
                {
                    responseType: 'blob' as 'json',
                    observe: 'response'
                }
            )
                .subscribe({
                    next: (response: any): void => {
                        const data = response.body;
                        const headers = response.headers;

                        const filename = headers.get('x-filename');
                        const contentType = headers.get('content-type');

                        const linkElement = document.createElement('a');
                        try {
                            const blob = new Blob([<BlobPart>data], { type: contentType });
                            const url = window.URL.createObjectURL(blob);

                            linkElement.setAttribute('href', url);
                            linkElement.setAttribute('download', filename);

                            const clickEvent = new MouseEvent('click', {
                                'view': window,
                                'bubbles': true,
                                'cancelable': false
                            });
                            linkElement.dispatchEvent(clickEvent);
                            observer.next();
                            observer.complete();
                        } catch (error) {
                            this.logService.debug(
                                'ApiService:downloadFile()',
                                error.message
                            );
                            observer.error(error);
                            observer.complete();
                        }
                    },
                    error: (error: any): void => {
                        this.logService.error('SupportApiService.downloadFile()', 'Failed to download file.', error);
                    }
                });
        });
    }
}
